/**
 * NT2 · খবরের পছন্দ — `/account/notifications`
 *
 * Transcribed from `design/Oprappo P2 Projects and Explore.dc.html`, frame NT2
 * (360 × 680), lines 234–306.
 *
 * WHY THIS LIVES IN `routes/account-notifications/` AND NOT `routes/account/`:
 * WP-D owns `apps/web/src/routes/account/**` and has already built F1–F6 there. Two
 * packages writing one directory is the merge conflict docs/10 § rules exists to
 * prevent, so the route path is `/account/notifications` and the directory is its own.
 * The router mounts it; nothing in `routes/account/` is touched.
 *
 * ── FIDELITY NOTES ───────────────────────────────────────────────────────────
 * · The header is `../offering/chrome.js`'s `ScreenHeader`, not a re-transcription. NT2
 *   line 235 draws `background:#fff;border-bottom:1px solid #E2DED5;padding:14px 16px;
 *   display:flex;gap:12px;align-items:center` with a 20px back chevron and a
 *   `600 18px/1.75` ink title — the shared chrome, value for value. It carries WP-D's
 *   already-reported deviation: the design system's `chevronLeft` is `M14 6l-6 6 6 6`
 *   and this frame draws `M15 5l-7 7 7 7`, one pixel apart at the same 24-grid and 2px
 *   weight. Not reconciled here either.
 * · The back control's accessible name comes from `ariaLabel.back` via `IconButton`, so
 *   `পিছনে যান` is used rather than invented (docs/01 § accessible names).
 * · The four chip styles are the frame's, and two modes share one: সাপ্তাহিক (line 281)
 *   and নীরব (line 289) are both `border:1px solid #E2DED5;color:#5B6B69`. Style encodes
 *   intensity — solid teal for সঙ্গে সঙ্গে, outlined teal for দৈনিক, outlined grey for
 *   the two quiet ones — which is why the chip is not a per-mode colour lookup.
 * · No radius anywhere. The chips are `padding:6px 12px` rectangles, not pills: docs/02
 *   allows `999px` for chips, and this frame does not use it, so neither does this file.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ── WHY THIS SCREEN IS RED AT 3.45%, AND WHY NOTHING HERE WILL FIX IT ────────
 * Measured, element by element, against the frame: every box in this file lands where
 * the frame draws it, to within one constant — the built screen sits exactly 0.297px
 * BELOW the design capture, everywhere, uniformly.
 *
 * That constant is the harness, not the screen. `capture-design.mjs` clips the frame at
 * `Math.round(r.top)`, and NT2 sits at a fractional y in the P2 canvas (-0.297; NT1 and
 * EX1 share its row, B5–B7 sit at -0.297 in the P0 file, F1–F4 at +0.438, A1–A4 at
 * +0.063 — it is wherever the preceding rows' captions happen to leave it). The built
 * route renders at y=0 exactly. So the two images are a rigid 0.297px apart.
 *
 * Two independent measurements, each moving ONE side and nothing else:
 *   · design capture re-clipped on a whole-pixel boundary, build untouched → 0.00%, 2 px
 *   · build shifted up 0.297px, design capture untouched                  → 0.00%, 2 px
 * (2 of 244,800. There is no third thing wrong with this screen.)
 *
 * Why NT2 and not the other 58: a 0.3px shift is invisible on fills and is filtered out
 * on diagonal strokes — `compare.mjs` runs pixelmatch with `includeAA:false`, which is
 * also why the back-chevron deviation noted above scores nothing. It is NOT filtered on
 * a horizontal 1px rule, which simply lands on two different rows at two different
 * alphas and counts across its whole width. NT2 draws nine of them in 680px — six card
 * tops and bottoms, the header rule, the quiet-hours divider, the footer rule — and
 * 5,900 of the 8,453 differing pixels are those bands (rows 60-61, 142-143, 219-220,
 * 231-232, 308-309, 320-321, 533-534, 545-546, 608-609), the rest antialiasing on the
 * 13-15px Bangla between them. NT1, same canvas row and same 0.297px, is a list with
 * fewer rules and scores 1.97% — under the threshold by three hundredths.
 *
 * So the fix is one line in `tools/visual-regression/capture-design.mjs`: land the frame
 * on a whole pixel before clipping (a fractional `margin-top` on the canvas root does
 * it; `window.scrollBy` does not — Chromium snaps scroll offsets). That file is out of
 * this work package. Nothing in `routes/account-notifications/` can move a design-side
 * clip origin, and the only way to make this row green from here would be to push the
 * built screen off y=0 by 0.297px to chase the capture — which is buying a green row,
 * not earning one. Reported red at 3.45%.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ── WIRED ────────────────────────────────────────────────────────────────────
 * `GET /v1/notifications/prefs` supplies the title, the intro, the six rows in the
 * frame's order, the four mode names for the picker, the quiet-hours switch and the
 * footer. `PATCH` to the same path writes one category's mode and
 * `PATCH /prefs/quiet-hours` writes the switch; both answer with the whole payload, which
 * is what re-renders.
 *
 * SIX ROWS, NOT NINE, and the SERVER decides which six. `prefs` in the same response
 * carries all nine categories resolved — a preference that exists and cannot be read is
 * a preference that silently stops being honoured — and `screen.rows` is what NT2 draws.
 * Nine rows overflow this 680px frame, whose list is `overflow:hidden`, and three of them
 * would need labels and chip states nobody drew
 * (`apps/api/src/notifications/screens.ts § NT2_ROW_ORDER`).
 *
 * ── THE MODE PICKER, WHICH THE FRAME DOES NOT DRAW ───────────────────────────
 * NT2 draws the chips and no way to change one. Four modes per category have to be
 * reachable (docs/13 § UI), so tapping a chip opens `@oprappo/ui`'s § 04 `Sheet` — the
 * design system's own "choose one of N" overlay, whose frame note reads "Sheets for
 * choosing, modals for confirming". Its title is the row's own drawn label and its four
 * options are the four drawn mode names, so the picker introduces NO new Bangla.
 * Flagged as an interaction this build chose, not one the frame specifies.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet } from '@oprappo/ui';
import { Frame, SAND_FRAME, ScreenHeader } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import type { NotifMode } from '../notifications/types.js';
import {
  fetchPrefs,
  setPref,
  setQuietHours,
  type PrefScreenRow,
  type PrefsResponse,
} from '../notifications/api.js';

/**
 * The chip, lines 248 / 257 / 265 / 273 / 281 / 289.
 *
 * Every variant is `padding:6px 12px;font:500 13px/1.75 'Anek Bangla';flex:none`. Only
 * fill, border and colour differ.
 */
function chipClass(mode: NotifMode, locked: boolean): string {
  const base =
    'px-[12px] py-[6px] font-anek font-medium text-[13px] leading-[1.75] flex-none rounded-none';
  if (locked) return `${base} border border-border bg-sand-deep text-muted`; // line 257
  if (mode === 'immediate') return `${base} bg-teal text-white border-0`; // line 248
  if (mode === 'daily_digest') return `${base} border border-teal text-teal`; // line 273
  return `${base} border border-border text-muted`; // lines 281 and 289
}

function Row({
  row,
  onPick,
}: {
  row: PrefScreenRow;
  onPick: (() => void) | undefined;
}): JSX.Element {
  // Both the mode and the word for it come from the server, including the fifth label
  // `সবসময়` that `critical` carries and that is not one of the four modes.
  const { mode, mode_label_bn: label } = row;
  return (
    // `background:#fff;border:1px solid #E2DED5;padding:13px 14px`
    <div className="bg-white border border-border px-[14px] py-[13px]">
      <div className="flex justify-between items-center gap-[11px]">
        <div className="flex-1">
          <div className="font-anek font-semibold text-[15px] leading-[1.75] text-ink">
            {row.label_bn}
          </div>
          {row.hint_bn === null ? null : (
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[1px]">
              {row.hint_bn}
            </div>
          )}
        </div>
        {row.locked ? (
          // Drawn as a chip, not a control. Shown as unchangeable rather than hidden, so
          // it is rendered as text with `aria-disabled` and no button semantics at all —
          // a disabled button is still announced as a button the user might try.
          <div className={chipClass(mode, true)} aria-disabled="true">
            {label}
          </div>
        ) : (
          <button type="button" onClick={onPick} className={chipClass(mode, false)}>
            {label}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Line 299 — `width:44px;height:26px;background:#0E3A45;display:flex;align-items:center;
 * justify-content:flex-end;padding:0 3px` with a `20 × 20` white knob.
 *
 * Only the ON state is drawn. The OFF state moves the knob to `justify-start` over
 * `#EDEAE1`, which is the design system's own recessed surface — reported, because the
 * frame does not draw it.
 *
 * `box-content` is load-bearing, for the same reason `routes/offering/chrome.js`'s
 * `Radio` carries it: the P2 canvas sets `box-sizing:border-box` on the frame boxes and
 * nowhere else, so `width:44px` + `padding:0 3px` is a FIFTY-pixel track there, measured
 * at `left:293 width:50` against the row's right edge at 343. This app's reset makes
 * everything border-box, which drew it 44px — 6px narrow on the left, with the knob in
 * the right place and the track short behind it. The row sits at y=644 inside a
 * `flex:1;overflow:hidden` region that ends at y=608, so it is clipped out of the 680px
 * frame on both sides and the visual-regression number does not move; it is corrected
 * because it is wrong, not because it scores.
 */
function Toggle({
  on,
  labelBn,
  onToggle,
}: {
  on: boolean;
  labelBn: string;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={labelBn}
      onClick={onToggle}
      className={
        'box-content w-[44px] h-[26px] flex items-center px-[3px] py-0 flex-none border-0 rounded-none ' +
        (on ? 'bg-teal justify-end' : 'bg-sand-deep justify-start')
      }
    >
      <span className="w-[20px] h-[20px] bg-white block" />
    </button>
  );
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what `GET /v1/notifications/prefs` returned and renders it; the
 * container below does the loading. That split is what lets the fidelity test render this
 * screen from `fixtures/api/notifications.nt2-prefs.json` — the same file the API's own
 * suite asserts its response against.
 */
export function NT2PreferencesView({
  data,
  onPick,
  onQuietHours,
}: {
  data: PrefsResponse['screen'] | null;
  onPick: (row: PrefScreenRow, mode: NotifMode) => void;
  onQuietHours: (enabled: boolean) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [picking, setPicking] = useState<PrefScreenRow | null>(null);

  return (
    // The wrapper exists only to give the sheet a positioning context, and it has to be
    // the same width as the frame inside it or the sheet anchors to the wrong box. It
    // said `w-[360px]`; now it tracks `FRAME_BOX`'s cap, so a phone wider than 360 gets
    // the sheet over the screen rather than over a 360px slice of it.
    <div className="relative w-full max-w-[430px] mx-auto">
    <Frame className={SAND_FRAME}>
      <ScreenHeader titleBn={data?.title_bn ?? ''} onBack={() => navigate(-1)} />

      {/* `flex:1;padding:16px;overflow:hidden` */}
      <div className="flex-1 p-[16px] oprappo-scroll">
        <p className="font-anek font-normal text-[14px] leading-[1.75] text-body mb-[16px] m-0">
          {data?.intro_bn ?? ''}
        </p>

        <div className="flex flex-col gap-[11px]">
          {(data?.rows ?? []).map((row) => (
            <Row
              key={row.category}
              row={row}
              onPick={row.locked ? undefined : () => setPicking(row)}
            />
          ))}
        </div>

        {/* `border-top:1px solid #E2DED5;margin-top:18px;padding-top:15px` */}
        <div className="border-t border-border mt-[18px] pt-[15px]">
          <div className="flex justify-between items-center gap-[11px]">
            <div className="flex-1">
              <div className="font-anek font-semibold text-[15px] leading-[1.75] text-ink">
                {data?.quiet_hours.label_bn ?? ''}
              </div>
              <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[1px]">
                {data?.quiet_hours.hint_bn ?? ''}
              </div>
            </div>
            <Toggle
              on={data?.quiet_hours.enabled ?? true}
              labelBn={data?.quiet_hours.label_bn ?? ''}
              onToggle={() => onQuietHours(!(data?.quiet_hours.enabled ?? true))}
            />
          </div>
        </div>
      </div>

      {/* Line 303 — `background:#F4F0E7;border-top:1px solid #E2DED5;padding:12px 16px` */}
      <div className="bg-sand border-t border-border px-[16px] py-[12px]">
        <div className="font-anek font-normal text-[13px] leading-[1.75] text-body">
          {data?.footer_bn ?? ''}
        </div>
      </div>

    </Frame>

      {picking === null ? null : (
        <div className="absolute inset-x-0 bottom-0">
          <Sheet
            titleBn={picking.label_bn}
            options={(data?.modes ?? []).map((m) => ({ id: m.id, labelBn: m.label_bn }))}
            selectedId={picking.mode}
            onSelect={(id) => {
              onPick(picking, id as NotifMode);
              setPicking(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

export function NT2Preferences(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchPrefs(signal), []);
  const { data, error } = useResource(load, 'prefs');

  // Every write answers with the whole payload and that answer is what renders. A chip
  // flipped locally is a chip that says দৈনিক while the server has সঙ্গে সঙ্গে — and
  // `critical` is refused by the route, so a client that flipped it optimistically would
  // show a choice that was never made.
  const [saved, setSaved] = useState<PrefsResponse | null>(null);
  const screen = (saved ?? data)?.screen ?? null;

  return (
    <SurfaceStates surface="account" error={error ?? undefined}>
      <NT2PreferencesView
        data={screen}
        onPick={(row, mode) => void setPref(row.category, mode).then(setSaved)}
        onQuietHours={(enabled) => void setQuietHours(enabled).then(setSaved)}
      />
    </SurfaceStates>
  );
}
